import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';

import FileSaver from 'file-saver';
import JSZip from 'jszip';
import { DynamicUrl } from 'src/app/commons/url';
import { SettingField } from 'src/app/interfaces/database';
import { Gift } from 'src/app/interfaces/gift';
import { Group } from 'src/app/interfaces/group';
import { Guest } from 'src/app/interfaces/guest';
import { Seating } from 'src/app/interfaces/seating';
import { ExportType, SettingFieldType } from 'src/app/types/general';
import writeXlsxFile from 'write-excel-file';
import { AccountInfoService } from '../account/account-info.service';
import { AccountUserService } from '../account/account-user.service';
import { FunctionService } from '../general/function.service';
import { LinkService } from '../general/link.service';
import { PopupService } from '../general/popup.service';
import { GiftService } from '../gift/gift.service';
import { GroupService } from '../group/group.service';
import { QrcodeService } from '../qrcode/qrcode.service';
import { SeatingService } from '../seating/seating.service';
import { SessionService } from '../setting/session.service';
import { SettingFieldService } from '../setting/setting-field.service';
import { GuestListService } from './guest-list.service';
import { GuestService } from './guest.service';

import { BlastMsg } from 'src/app/interfaces/blast';
import { BlastMsgService } from '../blast/blast-msg.service';
import { DateTimeService } from '../general/date-time.service';
import { CheckinSettingService } from '../checkin/checkin-setting.service';
import { SeatingSettingService } from '../seating/seating-setting.service';
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';
import { VisitorLogoService } from '../visitor/visitor-logo.service';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class GuestExportService implements OnInit, OnDestroy {

  constructor(
    private http: HttpClient,
    private platform: Platform,
    private translate: TranslateService,
    private guestService: GuestService,
    private guestListService: GuestListService,
    private groupService: GroupService,
    private giftService: GiftService,
    private seatingService: SeatingService,
    private sessionService: SessionService,
    private accountInfoService: AccountInfoService,
    private accountUserService: AccountUserService,
    private checkinSettingService: CheckinSettingService,
    private seatingSettingService: SeatingSettingService,
    private qrcodeService: QrcodeService,
    private dateTimeService: DateTimeService,
    private settingFieldService: SettingFieldService,
    private visitorLogoService: VisitorLogoService,
    private blastMsgService: BlastMsgService,
    private linkService: LinkService,
    private functionService: FunctionService,
    private popupService: PopupService,
  ) { }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
  }

  async download(exportType: ExportType) {
    await this.popupService.presentLoading(this.translate.instant('LOADING.export'));
    if (exportType === 'qrcode') {
      await this.generateQrcodeZip();
    } else if (exportType === 'guestList') {
      await this.generateGuestList();
    } else if (exportType === 'checkin') {
      await this.generateCheckinList();
    } else if (exportType === 'gift') {
      await this.generateGiftList();
    } else if (exportType === 'fullReport') {
      await this.generateFullReport();
    }
    await this.functionService.delay(500);
    await this.popupService.dismissLoading();
    // if (!this.platform.is('hybrid') || true) {

    // } else {
    //   this.promptOpenWeb();
    // }
  }

  async promptOpenWeb() {
    const confirm = await this.popupService.presentConfirm(
      this.translate.instant('EXPORT.msg.web')
    );
    confirm.onWillDismiss().then((result: any) => {
      if (result?.data?.confirm) {
        this.linkService.openUrl(DynamicUrl.long.home, true);
      } else {

      }
    });
  }

  format(timestamp: number, dateTimeFormat?: string, timezone?: string) {
    if (!timezone && this.accountInfoService?.accountInfo?.timezone?.name) {
      timezone = this.accountInfoService.accountInfo.timezone.name;
    }
    return this.dateTimeService.format(timestamp, dateTimeFormat, '', timezone);
  }

  //#region Generate Report
  async generateFullReport() {
    const guestList = [ ...this.guestListService.generateGroupList(this.guestService.getGuestList('guest')) ];
    const giftList = [ ...this.giftService.getGiftList() ];
    const fileName = this.translate.instant('EXPORT.full_report.file', {
        title: (this.accountInfoService?.accountTitle ? this.accountInfoService.accountTitle : '') + ' ' +  Date.now()
    });
    const blob = await writeXlsxFile([ this.generateGuestListData(guestList), this.generateSeatingViewData(guestList), this.generateCheckinListData(guestList), this.generateGiftListData(giftList) ], {
      // fileName: this.translate.instant('EXPORT.full_report.file', {
      //   title: (this.accountInfoService?.accountTitle ? this.accountInfoService.accountTitle : '') + ' ' +  Date.now()
      // }),
      stickyRowsCount: 1,
      stickyColumnsCount: 3,
      columns: [ this.generateGuestListColumn(), this.generateSeatingViewColumn(), this.generateCheckinListColumn(), this.generateGiftColumn() ],
      sheets: [ this.translate.instant('EXPORT.guest_list.sheet.list'), this.translate.instant('EXPORT.guest_list.sheet.seating'), this.translate.instant('EXPORT.checkin.sheet.list'), this.translate.instant('MODULE.list.gift') ],
      headerStyle: {
        backgroundColor: '#eeeeee',
        wrap: true,
        fontWeight: 'bold'
      },
      orientation: 'landscape'
    });

    if (blob) {
      try {
        if (this.platform.is('hybrid')) {
          const data = await this.functionService.blobToBase64(blob);
          if (data) {
            const result = await Filesystem.writeFile({
              path: fileName,
              data,
              directory: Directory.Documents,
              // encoding: Encoding.UTF8,
            });
            if (result?.uri) {
              await FileOpener.open({ 
                filePath: result?.uri, 
                contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
              });
            }
          }
        } else {
          FileSaver.saveAs(blob, fileName);
        }
      } catch (err) {
        console.error(err);
      } 
    }
    await this.popupService.presentToast(this.translate.instant('MSG.action_done'), 'success');
  }

  async generateGuestList() {
    const guestList = [ ...this.guestListService.generateGroupList(this.guestService.getGuestList('guest')) ];
    
    const fileName = this.translate.instant('EXPORT.guest_list.file', {
        title: (this.accountInfoService?.accountTitle ? this.accountInfoService.accountTitle : '') + ' ' +  Date.now()
    });
    const blob = await writeXlsxFile([ this.generateGuestListData(guestList), this.generateSeatingViewData(guestList) ], {
      // fileName: this.translate.instant('EXPORT.guest_list.file', {
      //   title: (this.accountInfoService?.accountTitle ? this.accountInfoService.accountTitle : '') + ' ' +  Date.now()
      // }),
      stickyRowsCount: 1,
      stickyColumnsCount: 3,
      columns: [ this.generateGuestListColumn(), this.generateSeatingViewColumn() ],
      sheets: [ this.translate.instant('EXPORT.guest_list.sheet.list'), this.translate.instant('EXPORT.guest_list.sheet.seating') ],
      headerStyle: {
        backgroundColor: '#eeeeee',
        wrap: true,
        fontWeight: 'bold'
      },
      orientation: 'landscape'
    });
    
    if (blob) {
      try {
        if (this.platform.is('hybrid')) {
          const data = await this.functionService.blobToBase64(blob);
          if (data) {
            const result = await Filesystem.writeFile({
              path: fileName,
              data,
              directory: Directory.Documents,
              // encoding: Encoding.UTF8,
            });
            if (result?.uri) {
              await FileOpener.open({ 
                filePath: result?.uri, 
                contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
              });
            }
          }
        } else {
          FileSaver.saveAs(blob, fileName);
        }
      } catch (err) {
        console.error(err);
      } 
    }
    await this.popupService.presentToast(this.translate.instant('MSG.action_done'), 'success');
  }

  async generateCheckinList() {
    const guestList = [ ...this.guestListService.generateGroupList(this.guestService.getGuestList('checkin')) ];
    const fileName = this.translate.instant('EXPORT.checkin.file', {
        title: (this.accountInfoService?.accountTitle ? this.accountInfoService.accountTitle : '') + ' ' +  Date.now()
    });
    const blob = await writeXlsxFile([ this.generateCheckinListData(guestList) ], {
      // fileName: this.translate.instant('EXPORT.checkin.file', {
      //   title: (this.accountInfoService?.accountTitle ? this.accountInfoService.accountTitle : '') + ' ' +  Date.now()
      // }),
      stickyRowsCount: 1,
      stickyColumnsCount: 3,
      columns: [ this.generateCheckinListColumn() ],
      sheets: [ this.translate.instant('EXPORT.checkin.sheet.list') ],
      headerStyle: {
        backgroundColor: '#eeeeee',
        wrap: true,
        fontWeight: 'bold'
      },
      orientation: 'landscape'
    });
    
    if (blob) {
      try {
        if (this.platform.is('hybrid')) {
          const data = await this.functionService.blobToBase64(blob);
          if (data) {
            const result = await Filesystem.writeFile({
              path: fileName,
              data,
              directory: Directory.Documents,
              // encoding: Encoding.UTF8,
            });
            if (result?.uri) {
              await FileOpener.open({ 
                filePath: result?.uri, 
                contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
              });
            }
          }
        } else {
          FileSaver.saveAs(blob, fileName);
        }
      } catch (err) {
        console.error(err);
      } 
    }
    this.popupService.presentToast(this.translate.instant('MSG.action_done'), 'success');
  }
  
  async generateGiftList() {
    const giftList = [ ...this.giftService.getGiftList() ];

    const fileName = this.translate.instant('EXPORT.gift.file', {
        title: (this.accountInfoService?.accountTitle ? this.accountInfoService.accountTitle : '') + ' ' +  Date.now()
    });
    const blob = await writeXlsxFile([ this.generateGiftListData(giftList) ], {
      // fileName: this.translate.instant('EXPORT.gift.file', {
      //   title: (this.accountInfoService?.accountTitle ? this.accountInfoService.accountTitle : '') + ' ' +  Date.now()
      // }),
      stickyRowsCount: 1,
      stickyColumnsCount: 3,
      columns: [ this.generateGiftColumn() ],
      sheets: [ this.translate.instant('MODULE.list.gift') ],
      headerStyle: {
        backgroundColor: '#eeeeee',
        wrap: true,
        fontWeight: 'bold'
      },
      orientation: 'landscape'
    });
    
    if (blob) {
      try {
        if (this.platform.is('hybrid')) {
          const data = await this.functionService.blobToBase64(blob);
          if (data) {
            const result = await Filesystem.writeFile({
              path: fileName,
              data,
              directory: Directory.Documents,
              // encoding: Encoding.UTF8,
            });
            if (result?.uri) {
              await FileOpener.open({ 
                filePath: result?.uri, 
                contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
              });
            }
          }
        } else {
          FileSaver.saveAs(blob, fileName);
        }
      } catch (err) {
        console.error(err);
      } 
    }
    await this.popupService.presentToast(this.translate.instant('MSG.action_done'), 'success');
  }
  //#endregion

  //#region Get Data
  /**
   * Get list of setting field value
   * @param settingFieldType Setting field type
   * @param settingFieldList Setting field list
   * @returns Join of setting field value
   */
  getSettingField(settingFieldType: SettingFieldType, settingFieldList: SettingField[], showCount?: boolean): string {
    return settingFieldType && settingFieldList?.length ?
    this.settingFieldService.joinSettingField(settingFieldType, settingFieldList, false, showCount) : '';
  }

  /**
   * Get group name by group id
   * @param groupId group id
   * @returns group name
   */
  getGroupName(groupId: string): string {
    return groupId ? this.groupService.getGroupName(groupId) : '';
  }

  getInvitedByFolder(invitedBy: SettingField) {
    const invitedByFolder = invitedBy?.custom ? invitedBy.value : this.translate.instant('LIST.invited_by.' + invitedBy.value);
    return invitedByFolder;
  }

  getCategoryFolder(category: SettingField) {
    const categoryFolder = category?.custom ? category.value : this.translate.instant('LIST.category.' + category.value);
    return categoryFolder;
  }

  getSessionFolder(session: SettingField) {
    const sessionFolder = session?.custom ? session.value : this.translate.instant('LIST.session.' + session.value);
    return sessionFolder;
  }

  getUserNameByUid(uid: string): string {
    if (uid) {
      return this.accountUserService.getUserByUid(uid)?.name;
    } else {
      return null;
    }
  }

  getSeatingTypeName(): string {
    return this.seatingSettingService.getSeatingTypeName();
  }

  checkSessionEnable() {
    const sessionList = this.sessionService.list.filter((x: SettingField) => {
      return x?.value && x.value !== 'none' && !x?.custom;
    });
    return sessionList?.length ? true : false;
  }

  //#endregion

  //#region Generate Report Header
  generateGuestListHeader(): any[] {
    const header = [
      {
        value: '',
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GROUP.lbl.name'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.lbl.guest_name'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.lbl.guest_nickname'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.lbl.pax'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.lbl.invited_by'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('LBL.category'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('TEMPLATE.variable.guest_seating'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.lbl.dietary_req'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.lbl.special_req'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('MOBILE.lbl.no'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.title.attending'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.lbl.remark'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      }
    ];
    return header;
  }

  generateSeatingHeader(value: string): any[] {
    const seatingHeader = [
      {
        value: value ? this.translate.instant('SEATING.lbl.name', { seating: this.getSeatingTypeName() }) + ': ' + value : this.translate.instant('SEATING.lbl.not_yet_assigend_guest'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        span: 9,
      }
    ];
    return seatingHeader;
  }

  generateSeatingGuestHeader(): any[] {
    const header = [
      {
        value: '',
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GROUP.lbl.name'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.lbl.guest_name'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.lbl.guest_nickname'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.lbl.pax'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.lbl.invited_by'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('LBL.category'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.lbl.dietary_req'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.lbl.special_req'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('MOBILE.lbl.no'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.title.attending'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.lbl.remark'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      }
    ];
    return header;
  }

  generateCheckinListHeader(): any[] {
    const header = [
      {
        value: '',
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GROUP.lbl.name'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.lbl.guest_name'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.lbl.guest_nickname'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.lbl.pax'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.lbl.invited_by'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('LBL.category'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('TEMPLATE.variable.guest_seating'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.lbl.dietary_req'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.lbl.special_req'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('MOBILE.lbl.no'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.lbl.remark'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('CHECKIN.lbl.status'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('CHECKIN.lbl.by'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('CHECKIN.lbl.time'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      }
    ];

    if (this.checkinSettingService.checkinSetting?.giftRecord) {
      header.push({
        value: this.translate.instant('GIFT.receive.status'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GIFT.receive.by'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GIFT.receive.time'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      });
    }
    return header;
  }

  generateGiftListHeader(): any[] {
    const header = [
      {
        value: '',
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GIFT.lbl.type'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GIFT.lbl.name'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('LBL.currency'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('LBL.amount'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GIFT.lbl.qty'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GROUP.lbl.name'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.lbl.guest_name'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.lbl.guest_nickname'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.lbl.invited_by'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('LBL.category'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('TEMPLATE.variable.guest_seating'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.lbl.dietary_req'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.lbl.special_req'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('MOBILE.lbl.no'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('GUEST.lbl.remark'),
        fontWeight: 'bold',
        wrap: true,
        align: 'center',
        alignVertical: 'center',
        borderStyle: 'thin',
      }
    ];
    return header;
  }
  //#endregion

  //#region Generate Report Column
  generateGuestListColumn(): any[] {
    const columns = [
      { width: 4, wrap: true },
      { width: 18, wrap: true },
      { width: 18, wrap: true },
      { width: 8, wrap: true },
      { width: 7, wrap: true },
      { width: 8, wrap: true },
      { width: 18, wrap: true },
      { width: 7, wrap: true },
      { width: 8, wrap: true },
      { width: 8, wrap: true },
      { width: 15, wrap: true },
      { width: 13, wrap: true },
      { width: 7, wrap: true }
    ];
    return columns;
  }

  generateSeatingViewColumn() {
    const columns = [
      { width: 4, wrap: true },
      { width: 18, wrap: true },
      { width: 18, wrap: true },
      { width: 8, wrap: true },
      { width: 7, wrap: true },
      { width: 8, wrap: true },
      { width: 10, wrap: true },
      { width: 8, wrap: true },
      { width: 8, wrap: true },
      { width: 15, wrap: true },
      { width: 13, wrap: true },
      { width: 8, wrap: true }
    ];
    return columns;
  }

  generateCheckinListColumn(): any[] {
    const columns = [
      { width: 4, wrap: true },
      { width: 18, wrap: true },
      { width: 18, wrap: true },
      { width: 8, wrap: true },
      { width: 7, wrap: true },
      { width: 8, wrap: true },
      { width: 18, wrap: true },
      { width: 7, wrap: true },
      { width: 8, wrap: true },
      { width: 8, wrap: true },
      { width: 15, wrap: true },
      { width: 7, wrap: true },
      { width: 7, wrap: true },
      { width: 15, wrap: true },
      { width: 15, wrap: true },
    ];
    if (this.checkinSettingService.checkinSetting?.giftRecord) {
      columns.push(
        { width: 7, wrap: true },
        { width: 15, wrap: true },
        { width: 15, wrap: true }
      );
    }
    return columns;
  }

  generateGiftColumn() {
    const columns = [
      { width: 4, wrap: true },
      { width: 9, wrap: true },
      { width: 20, wrap: true },
      { width: 8, wrap: true },
      { width: 8, wrap: true },
      { width: 7, wrap: true },
      { width: 20, wrap: true },
      { width: 20, wrap: true },
      { width: 16, wrap: true },
      { width: 10, wrap: true },
      { width: 10, wrap: true },
      { width: 8, wrap: true },
      { width: 8, wrap: true },
      { width: 8, wrap: true },
      { width: 15, wrap: true },
      { width: 8, wrap: true }
    ];
    return columns;
  }
  //#endregion

  //#region Generate Row
  generateGuestListGroupRow(group: Group) {
    const row = [
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: group?.groupName,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: group?.memberList?.length ? group.memberList?.length : 1,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: group?.mobile?.no,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      }
    ];
    return row;
  }

  generateGuestListGuestRow(guest: Guest, counter: number, member?: boolean) {
    const row = [
      {
        value: counter,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.getGroupName(guest?.groupId),
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: guest.name,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: guest.nickname,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: member ? '' : 1,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.getSettingField('invited_by', guest?.invitedBy),
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.getSettingField('category', guest?.category),
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: guest?.seating,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.getSettingField('dietary_req', guest?.dietaryReq),
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.getSettingField('special_req', guest?.specialReq),
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: guest?.mobile?.no,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: guest?.status?.attending ? this.translate.instant('LIST.attending_status.' + guest.status.attending) : '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: guest?.remark,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      }
    ];
    return row;
  }

  generateSeatingGroupRow(group: Group) {
    const row = [
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: group?.groupName,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: group?.memberList?.length ? group.memberList?.length : 1,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: group?.mobile?.no,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      }
    ];

    return row;
  }

  generateSeatingGuestRow(guest: Guest, counter: number, member?: boolean) {
    const row = [
      {
        value: counter,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.getGroupName(guest?.groupId),
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: guest.name,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: guest.nickname,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: member ? '' : 1,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.getSettingField('invited_by', guest?.invitedBy),
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.getSettingField('category', guest?.category),
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.getSettingField('dietary_req', guest?.dietaryReq),
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.getSettingField('special_req', guest?.specialReq),
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: guest?.mobile?.no,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: guest?.status?.attending ? this.translate.instant('LIST.attending_status.' + guest.status.attending) : '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: guest?.remark,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      }
    ];
    return row;
  }

  generateCheckinListGroupRow(group: Group) {
    const row = [
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: group?.groupName,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: group?.memberList?.length ? group.memberList?.length : 1,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: group?.mobile?.no,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: group?.status?.checkin ? this.translate.instant('BTN.yes') : this.translate.instant('BTN.no'),
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: group?.status?.checkin && group?.checkinBy?.uid ? this.getUserNameByUid(group.checkinBy.uid) : '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: group?.status?.checkin && group?.checkinBy?.time?.seconds ? this.format(group.checkinBy.time.seconds * 1000) : '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      }
    ];

    if (this.checkinSettingService.checkinSetting?.giftRecord) {
      row.push({
        value: group?.status?.gift ? this.translate.instant('BTN.yes') : this.translate.instant('BTN.no'),
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: group?.status?.gift && group?.giftBy?.uid ? this.getUserNameByUid(group.giftBy.uid) : '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: group?.status?.gift && group?.giftBy?.time?.seconds ? this.format(group.giftBy.time.seconds * 1000) : '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      })
    }
    return row;
  }

  generateCheckinListGuestRow(guest: Guest, counter: number, member?: boolean) {  
    const row = [
      {
        value: counter,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.getGroupName(guest?.groupId),
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: guest.name,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: guest.nickname,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: member ? '' : 1,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.getSettingField('invited_by', guest?.invitedBy),
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.getSettingField('category', guest?.category),
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: guest?.seating,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.getSettingField('dietary_req', guest?.dietaryReq),
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.getSettingField('special_req', guest?.specialReq),
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: guest?.mobile?.no,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: guest?.remark,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: guest?.status?.checkin ? this.translate.instant('BTN.yes') : this.translate.instant('BTN.no'),
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: guest?.status?.checkin && guest?.checkinBy?.uid ? this.getUserNameByUid(guest.checkinBy.uid) : '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: guest?.status?.checkin && guest?.checkinBy?.time?.seconds ? this.format(guest.checkinBy.time.seconds * 1000) : '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      }
    ];

    if (this.checkinSettingService.checkinSetting?.giftRecord) {
      row.push({
        value: guest?.status?.gift ? this.translate.instant('BTN.yes') : this.translate.instant('BTN.no'),
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: guest?.status?.gift && guest?.giftBy?.uid ? this.getUserNameByUid(guest.giftBy.uid) : '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: guest?.status?.gift && guest?.giftBy?.time?.seconds ? this.format(guest.giftBy.time.seconds * 1000) : '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      });
    }
    return row;
  }

  generateGiftListGiftRow(gift: Gift, counter: number) {
    const row = [
      {
        value: counter,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.translate.instant('LIST.gift.' + gift.giftType),
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: gift.name,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: gift?.giftType === 'cash' ? gift.currency : '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: gift?.giftType === 'cash' ? gift.amount : '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: gift?.qty ? gift.qty : 1,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      }
    ];
    return row;
  }

  generateGiftListGuestRow(guest: Guest, counter: number, member?: boolean) {
    const row = [
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.getGroupName(guest?.groupId),
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: guest.name,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: guest.nickname,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.getSettingField('invited_by', guest?.invitedBy),
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.getSettingField('category', guest?.category),
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: guest?.seating,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.getSettingField('dietary_req', guest?.dietaryReq),
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: this.getSettingField('special_req', guest?.specialReq),
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: guest?.mobile?.no,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: guest?.remark,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      }
    ];
    return row;
  }

  generateGiftListGroupRow(group: Group) {
    const row = [
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: group?.groupName,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      }
    ];
    return row;
  }
  //#endregion

  //#region Generate Data
  generateGuestListData(guestList: Guest[]): any[] {
    const data: any[] = [];
    data.push(this.generateGuestListHeader());
    if (guestList?.length) {
      let counter = 0;
      guestList?.forEach((guest: Guest) => {
        if (guest?.group?.groupId && guest?.group?.memberList?.length) {
          const row = this.generateGuestListGroupRow(guest.group);
          data.push(row);
          guest?.group?.memberList?.forEach((memberId: string) => {
            const member: Guest = this.guestService.getGuest(memberId);
            if (member) {
              counter++;
              const memberRow = this.generateGuestListGuestRow(member, counter, true);
              data.push(memberRow);
            }
          });
        } else {
          counter++;
          const row = this.generateGuestListGuestRow(guest, counter);
          data.push(row);
        }
      });
    }
    return data;
  }

  generateSeatingGuestData(guestList: Guest[]): any[] {
    const data: any[] = [];
    if (guestList?.length) {
      let counter = 0;

      guestList?.forEach((guest: Guest) => {
        if (guest?.group?.groupId && guest?.group?.memberList?.length) {
          const row = this.generateSeatingGroupRow(guest.group);
          data.push(row);
          guest?.group?.memberList?.forEach((memberId: string) => {
            const member: Guest = this.guestService.getGuest(memberId);
            if (member) {
              counter++;
              const memberRow = this.generateSeatingGuestRow(member, counter, true);
              data.push(memberRow);
            }
          });
        } else {
          counter++;
          const row = this.generateSeatingGuestRow(guest, counter);
          data.push(row);
        }
      });
    }
    return data;
  }

  generateCheckinListData(guestList: Guest[]): any[] {
    const data: any[] = [];
    data.push(this.generateCheckinListHeader());
    if (guestList?.length) {
      let counter = 0;
      guestList?.forEach((guest: Guest) => {
        if (guest?.group?.groupId && guest?.group?.memberList?.length) {
          const row = this.generateCheckinListGroupRow(guest.group);
          data.push(row);
          guest?.group?.memberList?.forEach((memberId: string) => {
            const member: Guest = this.guestService.getGuest(memberId);
            if (member) {
              counter++;
              const memberRow = this.generateCheckinListGuestRow(member, counter, true);
              data.push(memberRow);
            }
          });
        } else {
          counter++;
          const row = this.generateCheckinListGuestRow(guest, counter);
          data.push(row);
        }
      });
    }
    return data;
  }

  generateSeatingViewData(guestList: Guest[]): any[] {
    let data: any[] = [];
    const seatingList: Seating[] = [ ...this.seatingService.seatingList ].filter((seating: Seating) => {
      return seating?.name ? true : false;
    });
    if (seatingList?.length) {
      seatingList?.forEach((seating: Seating, index: number) => {
        if (seating?.seatingId && seating?.name) {      
          const filterGuest = this.functionService.cloneDeep(guestList).filter((guest: Guest) => {
            if (guest?.group?.groupId && guest?.group?.memberList?.length) {
              guest.group.memberList = guest.group.memberList.filter((memberId: string) => {
                const member: Guest = this.guestService.getGuest(memberId);
                return member.seating === seating.name;
              });
              return guest.group.memberList.length ? true : false;
            } else {
              return guest.seating === seating.name;
            }
          });

          data.push(this.generateSeatingHeader(seating.name));
          data.push(this.generateSeatingGuestHeader());
          if (filterGuest?.length) {
            data = data.concat(this.generateSeatingGuestData(filterGuest));
          }
          if (seatingList.length > index + 1) {
            data.push([{ value: '', span: 9, borderStyle: 'none' }]);
          }
        }
      });
    }

    const unassignGuestList = guestList.filter((guest: Guest) => {
      if (guest?.group?.groupId && guest?.group?.memberList?.length) {
        guest.group.memberList = guest.group.memberList.filter((memberId: string) => {
          const member: Guest = this.guestService.getGuest(memberId);
          return member.seating === '';
        });
        return guest.group.memberList.length ? true : false;
      } else {
        return guest.seating === '';
      }
    });

    if (unassignGuestList?.length) {
      data.push([{ value: '', span: 9, borderStyle: 'none' }]);
      data.push(this.generateSeatingHeader(''));
      data.push(this.generateSeatingGuestHeader());
      data = data.concat(this.generateSeatingGuestData(unassignGuestList));
    }
    return data;
  }

  generateGiftListData(giftList: Gift[]): any[] {
    const data: any[] = [];
    data.push(this.generateGiftListHeader());
    if (giftList?.length) {
      let counter = 0;
      giftList?.forEach((gift: Gift) => {
        if (gift?.giftId) {
          counter++;
          data.push(this.generateGiftListGiftRow(gift, counter));
          if (gift?.guestIdList?.length) {
            const guestList = this.guestListService.generateGroupList(this.guestService.getGuestListById(gift.guestIdList));
            guestList?.forEach((guest: Guest) => {
              if (guest?.group?.groupId && guest?.group?.memberList?.length) {
                // const row = this.generateGiftListGroupRow(guest.group);
                // data.push(row);
                guest?.group?.memberList?.forEach((memberId: string) => {
                  const member: Guest = this.guestService.getGuest(memberId);
                  if (member) {
                    const memberRow = this.generateGiftListGuestRow(member, counter, true);
                    data.push(memberRow);
                  }
                });
              } else {
                const row = this.generateGiftListGuestRow(guest, counter);
                data.push(row);
              }
            });
          }
          if (gift?.groupIdList?.length) {
            gift?.groupIdList?.forEach((groupId: string) => {
              const group = this.groupService.getGroup(groupId);
              // console.log(group);
              // const row = this.generateGiftListGroupRow(group);
              // data.push(row);
              if (group?.memberList?.length) {
                const memberList = this.guestService.getGuestListById(group?.memberList);
                memberList?.forEach((guest: Guest) => {
                  const memberRow = this.generateGiftListGuestRow(guest, counter, true);
                  data.push(memberRow);
                });
              }
            });
          }
        }
      });
    }
    return data;
  }
  //#endregion

  //#region Generate QR Code

  getShowTxt(topTxtType: string, bottomTxtType: string, title: string, guest?: Guest, group?: Group) {
    const result = {
      topTxt: '',
      bottomTxt: '',
    };

    if (topTxtType) {
      if (topTxtType === 'title') {
        result.topTxt = title ? title : '';
      } else if (topTxtType === 'name') {
        result.topTxt = guest?.name ? guest.name : group?.groupName ? group.groupName : '';
      }
    }

    if (bottomTxtType) {
      if (bottomTxtType === 'title') {
        result.bottomTxt = title ? title : '';
      } else if (bottomTxtType === 'name') {
        result.bottomTxt = guest?.name ? guest.name : group?.groupName ? group.groupName : '';
      }
    }

    return result;
  }

  async generateQrcodeZip(topTxtType?: string, bottomTxtType?: string) {
    const zip = new JSZip();
    const title = this.accountInfoService.accountTitle;
    const guestList = [ ...this.guestListService.generateGroupList(this.guestService.getGuestList('guest')) ];
    const groupNameList: string[] = [];
    const sessionEnable = this.checkSessionEnable();

    let image = '';
    if (this.visitorLogoService.logo) {
      const result = await this.http.get(this.visitorLogoService.logo, { responseType: 'blob' }).toPromise();
      if (result) {
        image = await this.functionService.blobToBase64(result);
      }
    }

    for (const guest of guestList) {
      if (guest?.group?.groupId) {
        // const sessionFolderList: string[] = [];
        const group = guest.group;
        let groupName = group.groupName;
        if (groupNameList.indexOf(groupName) === -1){
          groupNameList.push(groupName);
        } else {
          groupNameList.push(groupName);
          const count = groupNameList.filter((x: string) => {
            return x === groupName;
          }).length;
          groupName = groupName + ' - ' + count;
        }
        let invitedByFolder = '';
        let categoryFolder = '';
        let duplicateNameCount = 0;

        const { topTxt, bottomTxt } = this.getShowTxt(topTxtType, bottomTxtType, title, null, group);
        const groupBlob = await this.createQrCodeStyling(this.groupService.getGroupUrl(group.groupId, group?.rsvp?.linkId), topTxt, bottomTxt, image) as Blob;

        const memberList = this.guestService.getGuestListById(group?.memberList);
        for (const member of memberList) {
          if (member?.guestId) {
            invitedByFolder = this.getInvitedByFolder(member?.invitedBy?.[0]);
            categoryFolder = this.getCategoryFolder(member?.category?.[0]);

            let name = member.name;
            const index = memberList.findIndex((x: Guest) => {
              return x?.guestId !== member?.guestId && x.name === member.name;
            });
            if (name === group.groupName || index !== -1) {
              duplicateNameCount++;
              name = name + ' - ' + duplicateNameCount;
            }

            const { topTxt, bottomTxt } = this.getShowTxt(topTxtType, bottomTxtType, title, member);
            const blob = await this.createQrCodeStyling(this.guestService.getGuestUrl(member.guestId, member?.rsvp?.linkId), topTxt, bottomTxt, image) as Blob;
            if (sessionEnable && member?.session?.length) {
              member?.session?.forEach(async (session: SettingField) => {
                const sessionFolder = this.getSessionFolder(session);
                await zip.folder(sessionFolder).folder(invitedByFolder).folder(categoryFolder).folder(groupName)
                .folder(this.translate.instant('QRCODE.lbl.guest_qrcode')).file(name + '.png', blob, { base64: false });

                await zip.folder(sessionFolder).folder(invitedByFolder).folder(categoryFolder).folder(groupName)
                .folder(this.translate.instant('QRCODE.lbl.group_qrcode')).file(name + '.png', groupBlob, { base64: false });
              });
              // const sessionFolder = this.getSessionFolder(member.session[0]);
              // if (sessionFolderList.indexOf(sessionFolder) === -1) {
              //   sessionFolderList.push(sessionFolder);
              // }
              // zip.folder(sessionFolder).folder(invitedByFolder).folder(categoryFolder).folder(groupName)
              // .folder(this.translate.instant('QRCODE.lbl.guest_qrcode')).file(name + '.png', blob, { base64: false });
            } else {
              await zip.folder(invitedByFolder).folder(categoryFolder).folder(groupName)
              .folder(this.translate.instant('QRCODE.lbl.guest_qrcode')).file(name + '.png', blob, { base64: false });

              await zip.folder(invitedByFolder).folder(categoryFolder).folder(groupName)
              .folder(this.translate.instant('QRCODE.lbl.group_qrcode')).file(group.groupName + '.png', groupBlob, { base64: false });
            }
          }
        }
        // if (group?.groupId && group?.groupName && invitedByFolder && categoryFolder) {
        //   const blob = await this.createQrCodeStyling(this.groupService.getGroupUrl(group.groupId)) as Blob;
        //   if (sessionFolderList?.length) {
        //     for (const sessionFolder of sessionFolderList) {
        //       zip.folder(sessionFolder).folder(invitedByFolder).folder(categoryFolder).folder(groupName)
        //     .folder(this.translate.instant('QRCODE.lbl.group_qrcode')).file(group.groupName + '.png', blob, { base64: false });
        //     }
        //   } else {
        //     zip.folder(invitedByFolder).folder(categoryFolder).folder(groupName)
        //     .folder(this.translate.instant('QRCODE.lbl.group_qrcode')).file(group.groupName + '.png', blob, { base64: false });
        //   }
        // }
      } else if (guest?.guestId) {
        const invitedByFolder = this.getInvitedByFolder(guest?.invitedBy?.[0]);
        const categoryFolder = this.getCategoryFolder(guest?.category?.[0]);
        const { topTxt, bottomTxt } = this.getShowTxt(topTxtType, bottomTxtType, title, guest);
        const blob = await this.createQrCodeStyling(this.guestService.getGuestUrl(guest.guestId, guest?.rsvp?.linkId), topTxt, bottomTxt, image) as Blob;
        if (sessionEnable && guest?.session?.length) {
          guest?.session?.forEach(async (session: SettingField) => {
            const sessionFolder = this.getSessionFolder(session);
            await zip.folder(sessionFolder).folder(invitedByFolder).folder(categoryFolder).file(guest.name + '.png', blob, { base64: false });
          });
          // const sessionFolder = this.getSessionFolder(guest.session[0]);
          // zip.folder(sessionFolder).folder(invitedByFolder).folder(categoryFolder).file(guest.name + '.png', blob, { base64: false });
        } else {
          await zip.folder(invitedByFolder).folder(categoryFolder).file(guest.name + '.png', blob, { base64: false });
        }
      }
    }

    
    // see FileSaver.js
    const fileName = this.translate.instant('MODULE.list.qrcode') + ' - ' + this.accountInfoService.accountTitle;
    if (this.platform.is('hybrid')) {
      const data = await zip.generateAsync({type: 'base64'});

      const result = await Filesystem.writeFile({
        path: fileName,
        data,
        directory: Directory.Documents,
        // encoding: Encoding.UTF8,
      });
      if (result?.uri) {
        await FileOpener.open({ 
          filePath: result?.uri, 
          contentType: 'application/zip' 
        });
      }
    } else {
      const content = await zip.generateAsync({type: 'blob'});
      await FileSaver.saveAs(content, fileName + '.zip');
    }
    
    await this.popupService.presentToast(this.translate.instant('MSG.action_done'), 'success');
  }

  async createQrCodeStyling(data: string, topTxt: string, bottomTxt: string, image: string) {
    const blob = await this.qrcodeService.generateQRcode(data, 512, 512, 25, 'png', false, true, image) as Blob;;
    if (topTxt || bottomTxt) {
      return await this.setupTextImage(blob, 100, 30, topTxt, bottomTxt);
    }
    return blob;
  }
  //#endregion



  async generateBlastMsg() {
    const fileName = this.accountInfoService?.accountTitle + '.xlsx';
    
    
    try {
      if (this.platform.is('hybrid')) {
        const blob = await writeXlsxFile(this.generateBlastMsgData(), {
          orientation: 'landscape'
        });
        const data = await this.functionService.blobToBase64(blob);
        if (data) {
          const result = await Filesystem.writeFile({
            path: fileName,
            data,
            directory: Directory.Documents,
            // encoding: Encoding.UTF8,
          });
          if (result?.uri) {
            await FileOpener.open({ 
              filePath: result?.uri, 
              contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
          }
        }
      } else {
        const blob = await writeXlsxFile(this.generateBlastMsgData(), {
          fileName: this.accountInfoService?.accountTitle + '.xlsx',
          orientation: 'landscape'
        });
      }
    } catch (err) {
      console.error(err);
    } 
    await this.popupService.presentToast(this.translate.instant('MSG.action_done'), 'success');
  }

  generateBlastMsgData() {
    const data: any[] = [];
    const blastMsgList = this.blastMsgService.blastMsgList;
    // data.push(this.generateBlastMsgHeader());
    if (blastMsgList?.length) {
      blastMsgList?.forEach((blastMsg: BlastMsg) => {
        if (blastMsg?.msgId && blastMsg?.enable) {
          const row = this.generateBlastMsgRow(blastMsg);
          data.push(row);
        }
      });
    }
    return data;
  } 

  generateBlastMsgRow(blastMsg: BlastMsg) {
    const row = [
      {
        value: blastMsg?.msgId,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: blastMsg?.mobile?.no,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: blastMsg?.createBy?.time?.seconds ? this.format(blastMsg.createBy.time.seconds * 1000) : '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: blastMsg?.sent?.status,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: blastMsg?.sent?.time?.seconds ? this.format(blastMsg.sent.time.seconds * 1000) : '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: blastMsg?.delivered?.status,
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: blastMsg?.delivered?.time?.seconds ? this.format(blastMsg.delivered.time.seconds * 1000) : '',
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      },
      {
        value: blastMsg?.content.join('\n\n'),
        wrap: true,
        align: 'left',
        alignVertical: 'center',
        borderStyle: 'thin',
      }
    ];
    return row;
  }

  async setupTextImage(blob: Blob, padding: number, maxFontSize: number, top?: string, bottom?: string) {
    // Convert the Blob to an Image element
    const image = await this.convertBlobToImage(blob);
    const textMargin = 15;

    // Create a canvas programmatically
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Could not get 2D context from the canvas');
    }

    // Set canvas dimensions to match the image plus padding
    canvas.width = image.width;
    canvas.height = image.height + (padding * 2);

    // Draw the original image with padding
    ctx.drawImage(image, 0, padding);

    // Set dynamic font size based on image width
    // const topFontSize = Math.min(image.width / 10, 30);  // Example scaling logic

    if (top) {
      const topFontSize =this.getOptimalFontSize(ctx, top, canvas.width - (textMargin * 2), padding, maxFontSize);
      // Vertical align text in the middle of the top padding area
      const topTextYPosition = (padding / 2) + (topFontSize / 2) + (textMargin / 2);
  
      ctx.font = `${topFontSize}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = 'black';
      
      // const textWidth = ctx.measureText(bottom).width;
      ctx.fillText(top, canvas.width / 2, topTextYPosition);
    }

    if (bottom) {
      const bottomFontSize =this.getOptimalFontSize(ctx, bottom, canvas.width - (textMargin * 2), padding, maxFontSize);

      // Optionally, vertical align text in the middle of the bottom padding area
      const bottomTextYPosition = canvas.height + (bottomFontSize / 2) - (padding / 2) - (textMargin / 2);
    
      ctx.font = `${bottomFontSize}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = 'black';

      // const textWidth = ctx.measureText(bottom).width;
      // Uncomment the next line to draw text at the bottom
      ctx.fillText(bottom, canvas.width / 2, bottomTextYPosition);
    }


    // Return the new image as a Blob
    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((newBlob) => {
        if (newBlob) {
          resolve(newBlob);  // Return the new Blob with the text added
        } else {
          reject(new Error('Failed to generate Blob from canvas'));
        }
      }, 'image/png');
    });
  }

  private convertBlobToImage(blob: Blob): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      const blobUrl = URL.createObjectURL(blob);

      image.onload = () => {
        URL.revokeObjectURL(blobUrl);  // Clean up memory
        resolve(image);
      };

      image.onerror = (error) => {
        reject(error);
      };

      image.src = blobUrl;
    });
  }

  // async blobToBase64(blob: Blob): Promise<string> {
  //   return new Promise((resolve, reject) => {
  //     const reader = new FileReader();
  //     reader.onloadend = () => resolve(reader.result?.toString().split(',')[1] || '');
  //     reader.onerror = () => reject('Failed to convert Blob to Base64.');
  //     reader.readAsDataURL(blob);
  //   });
  // }

   // Helper function to calculate optimal font size
  private getOptimalFontSize(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, maxHeight: number, maxFontSize: number): number {
    let fontSize = Math.min(maxHeight, maxFontSize);  // Start with the minimum of the maxHeight or maxFontSize
    
    do {
      ctx.font = `${fontSize}px Arial`;  // Apply the current font size
      const textMetrics = ctx.measureText(text);
      const textHeight = textMetrics.actualBoundingBoxAscent + textMetrics.actualBoundingBoxDescent;
      const textWidth = textMetrics.width;

      // If the text fits within the available space (both width and height), return the current font size
      if (textWidth <= maxWidth && textHeight <= maxHeight) {
        return fontSize;
      }

      // Otherwise, decrease the font size and try again
      fontSize -= 1;
    } while (fontSize > 0);

    return fontSize;  // Return the best possible font size
  }
}
